/**
 * Labstep
 *
 * @module components/Permission/Table/Folder
 * @desc Table showing sharing permissions to folders
 */

import { EntityActionRemove } from 'labstep-web/components/Entity/Action/Remove';
import { EntityActionShare } from 'labstep-web/components/Entity/Action/Share';
import { EntityTableParamsContainer as EntityTable } from 'labstep-web/components/Entity/Table';
import { FolderActionMenu } from 'labstep-web/components/Folder/Action/Menu';
import { GroupInitials } from 'labstep-web/components/Group/Initials';
import { UserModalPermissionsFolder } from 'labstep-web/components/User/Modal/PermissionsFolder';
import { ICONS } from 'labstep-web/constants/icons';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { Size } from 'labstep-web/core/Initials/types';
import { PopupContainer } from 'labstep-web/core/Popup';
import { Folder } from 'labstep-web/models/folder.model';
import type React from 'react';
import type { PermissionTableFolderProps } from './types';

export const PermissionTableFolder: React.FC<
  PermissionTableFolderProps
> = ({ entity, folders, actions, ...rest }) => (
  <EntityTable
    entities={folders}
    editableName
    withCheckbox={false}
    actionMenu={({ entity }) => (
      <FolderActionMenu
        folder={entity}
        folderType={entity.type}
        actions={actions}
      />
    )}
    primaryColumn={{
      header: 'Collection',
      content: (folder: Folder) => (
        <Flex
          vAlignContent="center"
          style={{ height: '100%', gap: '4px', fontWeight: 500 }}
        >
          <Icon name={ICONS[Folder.entityName].primary} />
          {folder.name}
        </Flex>
      ),
      cellProps: { style: { flex: 2 } },
    }}
    extraColumns={[
      {
        header: 'Shared with',
        content: (folder: Folder) => {
          if (
            !folder.permissions ||
            folder.permissions.length === 0
          ) {
            return null;
          }
          const groups = folder.permissions.map(
            (permission) => permission.group,
          );
          return (
            <Flex vAlignContent="center" style={{ gap: '10px' }}>
              <Flex style={{ gap: '2px' }}>
                {groups.map((group) => (
                  <PopupContainer
                    key={group.id}
                    content={group.name}
                    trigger={
                      <GroupInitials
                        key={group.id}
                        size={Size.mini}
                        group={group}
                      />
                    }
                  />
                ))}
              </Flex>
              <EntityActionShare
                entity={folder}
                actionComponentProps={{
                  type: 'text',
                  icon: null,
                  text: 'Manage',
                }}
              />
            </Flex>
          );
        },
        cellProps: { style: { flex: 4 } },
      },
      {
        header: 'Users with access',
        content: (folder: Folder) => (
          <UserModalPermissionsFolder folder={folder} />
        ),
        cellProps: { style: { flex: 3 } },
      },
      {
        header: '',
        content: (folder: Folder) => (
          <EntityActionRemove
            noConfirmation
            entity={folder}
            entityId={folder.guid}
            entityName={folder.entityName}
            parentName={entity.entityName}
            parentId={entity.id}
            actionComponentProps={{
              type: 'icon',
              icon: 'remove',
              elementProps: {
                popup: { content: 'Remove from collection' },
              },
            }}
          />
        ),
      },
    ]}
    {...rest}
  />
);
